/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled from 'styled-components';
import {
  useLayoutEffect,
  useCallback,
  useState,
  useRef,
  useResizeEffect,
  createPortal,
} from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import { THEME_CONSTANTS } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { trackAppInsightsEvent } from '../../../../../initAppInsights';
import { useConfig } from '../../app/config';
import { noop } from '../../utils/noop';
import { getTransforms, getOffset } from './utils';
import { Placement } from './constants';

const Container = styled.div.attrs(
  ({
    $offset: { x, y, width, height },
    fillWidth,
    fillHeight,
    placement,
    isRTL,
    invisible,
  }) => ({
    style: {
      transform: `translate(${x}px, ${y}px) ${getTransforms(placement, isRTL)}`,
      ...(fillWidth ? { width: `${width}px` } : {}),
      ...(fillHeight ? { height: `${height}px` } : {}),
      ...(invisible ? { visibility: `hidden` } : {}),
    },
  })
)`
  /*! @noflip */
  left: 0px;
  top: 0px;
  position: fixed;
  overflow-y: auto;
  z-index: ${({ isInDialog }) => (isInDialog ? '11' : '2')};
  max-height: calc(100vh - ${THEME_CONSTANTS.WP_ADMIN.TOOLBAR_HEIGHT}px);
`;

const POPUP_MEASUREMENT_TOLERANCE = 0.5;
const POPUP_POSITION_DIAGNOSTIC_THRESHOLD = 20;
const POPUP_POSITION_DIAGNOSTIC_WINDOW_MS = 1000;
const POPUP_POSITION_DIAGNOSTIC_COOLDOWN_MS = 60000;

let lastPopupPositionDiagnosticAt = -Infinity;

function getNow() {
  return window.performance?.now?.() || Date.now();
}

function formatMeasurement(value) {
  return typeof value === 'number' ? Number(value.toFixed(2)) : value;
}

function areMeasurementsEqual(value, nextValue) {
  if (value === nextValue) {
    return true;
  }
  if (typeof value !== 'number' || typeof nextValue !== 'number') {
    return false;
  }

  return Math.abs(value - nextValue) <= POPUP_MEASUREMENT_TOLERANCE;
}

function arePopupStatesEqual(state, nextState) {
  return (
    areMeasurementsEqual(state?.height, nextState.height) &&
    areMeasurementsEqual(state?.offset?.x, nextState.offset.x) &&
    areMeasurementsEqual(state?.offset?.y, nextState.offset.y) &&
    areMeasurementsEqual(state?.offset?.width, nextState.offset.width) &&
    areMeasurementsEqual(state?.offset?.height, nextState.offset.height)
  );
}

function shouldIgnoreScrollEvent(evt, anchor, dock) {
  if (evt?.type !== 'scroll' || !evt?.target?.nodeType) {
    return false;
  }

  const { target } = evt;
  if (
    target === document ||
    target === document.body ||
    target === document.documentElement
  ) {
    return false;
  }

  return (
    !target.contains(anchor?.current) &&
    (!dock?.current || !target.contains(dock.current))
  );
}

function Popup({
  anchor,
  dock,
  children,
  renderContents,
  placement = Placement.BOTTOM,
  spacing,
  isOpen,
  isInDialog = false,
  invisible,
  fillWidth = false,
  fillHeight = false,
  onPositionUpdate = noop,
}) {
  const [popupState, setPopupState] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [popupElement, setPopupElement] = useState(null);
  const popup = useRef(null);
  const popupStateRef = useRef(null);
  const positionDiagnosticRef = useRef({
    hasTracked: false,
    attempts: 0,
    windowStartedAt: 0,
  });
  const { isRTL } = useConfig();
  popupStateRef.current = popupState;

  const setPopupRef = useCallback((node) => {
    if (popup.current === node) {
      return;
    }

    popup.current = node;
    setPopupElement(node);
  }, []);

  const trackPotentialPositionLoop = useCallback(
    (reason, extraPayload = {}) => {
      if (!isOpen) {
        return;
      }

      const now = getNow();
      const diagnostic = positionDiagnosticRef.current;

      if (
        now - diagnostic.windowStartedAt >
        POPUP_POSITION_DIAGNOSTIC_WINDOW_MS
      ) {
        diagnostic.windowStartedAt = now;
        diagnostic.attempts = 0;
      }

      diagnostic.attempts += 1;

      if (
        diagnostic.hasTracked ||
        diagnostic.attempts < POPUP_POSITION_DIAGNOSTIC_THRESHOLD ||
        now - lastPopupPositionDiagnosticAt <
          POPUP_POSITION_DIAGNOSTIC_COOLDOWN_MS
      ) {
        return;
      }

      diagnostic.hasTracked = true;
      lastPopupPositionDiagnosticAt = now;

      const state = popupStateRef.current;
      const popupRect = popup.current?.getBoundingClientRect?.();

      void trackAppInsightsEvent('Studio: Popup Position Loop Detected', {
        reason,
        placement,
        attempts: diagnostic.attempts,
        windowMs: POPUP_POSITION_DIAGNOSTIC_WINDOW_MS,
        isRTL,
        hasAnchor: Boolean(anchor?.current),
        hasDock: Boolean(dock?.current),
        hasPopup: Boolean(popup.current),
        popupHeight: formatMeasurement(popupRect?.height),
        popupWidth: formatMeasurement(popupRect?.width),
        stateHeight: formatMeasurement(state?.height),
        stateX: formatMeasurement(state?.offset?.x),
        stateY: formatMeasurement(state?.offset?.y),
        stateWidth: formatMeasurement(state?.offset?.width),
        stateOffsetHeight: formatMeasurement(state?.offset?.height),
        ...extraPayload,
      }).catch(noop);
    },
    [anchor, dock, isOpen, isRTL, placement]
  );

  const positionPopup = useCallback(
    (evt) => {
      if (!mounted) {
        return;
      }

      // If scrolling within the popup, ignore.
      if (evt?.target?.nodeType && popup.current?.contains(evt.target)) {
        return;
      }
      if (shouldIgnoreScrollEvent(evt, anchor, dock)) {
        return;
      }
      if (!anchor?.current) {
        setPopupState(null);
        return;
      }

      const reason = evt?.type
        ? `positionPopup:${evt.type}`
        : typeof evt?.width === 'number' || typeof evt?.height === 'number'
        ? 'positionPopup:resize'
        : 'positionPopup';
      trackPotentialPositionLoop(reason);

      const nextState = {
        offset: getOffset(placement, spacing, anchor, dock, popup, isRTL),
        height: popup.current?.getBoundingClientRect()?.height,
      };

      setPopupState((state) =>
        arePopupStatesEqual(state, nextState) ? state : nextState
      );
    },
    [
      anchor,
      dock,
      placement,
      spacing,
      mounted,
      isRTL,
      trackPotentialPositionLoop,
    ]
  );

  const propagateDimensionChange = useCallback(
    () => positionPopup({ type: 'dimensionChange' }),
    [positionPopup]
  );

  useLayoutEffect(() => {
    if (!isOpen || !popupElement) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      positionPopup({ type: 'popupElement' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, popupElement, positionPopup]);

  useLayoutEffect(() => {
    setMounted(true);
    if (!isOpen) {
      positionDiagnosticRef.current.attempts = 0;
      positionDiagnosticRef.current.windowStartedAt = 0;
      return undefined;
    }
    positionPopup({ type: 'open' });

    // Adjust the position when scrolling.
    document.addEventListener('scroll', positionPopup, true);
    return () => document.removeEventListener('scroll', positionPopup, true);
  }, [isOpen, positionPopup]);

  useLayoutEffect(onPositionUpdate, [popupState, onPositionUpdate]);

  useResizeEffect({ current: document.body }, positionPopup, [positionPopup]);

  return popupState?.offset && isOpen
    ? createPortal(
        <Container
          ref={setPopupRef}
          fillWidth={fillWidth}
          fillHeight={fillHeight}
          placement={placement}
          isInDialog={isInDialog}
          isRTL={isRTL}
          $offset={popupState.offset}
          invisible={invisible}
        >
          {renderContents
            ? renderContents({ propagateDimensionChange })
            : children}
        </Container>,
        document.body
      )
    : null;
}

Popup.propTypes = {
  anchor: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    .isRequired,
  dock: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  children: PropTypes.node,
  renderContents: PropTypes.func,
  placement: PropTypes.oneOf(Object.values(Placement)),
  spacing: PropTypes.object,
  isOpen: PropTypes.bool,
  invisible: PropTypes.bool,
  fillWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  fillHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  onPositionUpdate: PropTypes.func,
};

export default Popup;
